import { useState, useEffect } from 'react';
import { X, Minus, Plus, Trash2, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useQuote } from '@/context/QuoteContext';
import { submitQuote, formatApiErrorDetail } from '@/lib/api';
import { QUOTE } from '@/constants/testIds';

export default function QuoteDrawer() {
  const {
    items, drawerOpen, closeDrawer,
    setQuantity, setUnit, remove, clear,
  } = useQuote();

  const [form, setForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset success/error each time drawer opens
    if (drawerOpen) {
      setError(null);
      setSuccess(null);
    }
  }, [drawerOpen]);

  // ESC to close
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const h = (e) => e.key === 'Escape' && closeDrawer();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [drawerOpen, closeDrawer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (items.length === 0) {
      setError('Add at least one item to your quote.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitQuote({
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          category: i.category,
          quantity: Number(i.quantity),
          unit: i.unit,
        })),
      });
      setSuccess(res);
      clear();
      setForm({ business_name: '', contact_name: '', email: '', phone: '', notes: '' });
      toast.success('Quote request sent — check your inbox');
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!drawerOpen}
        onClick={closeDrawer}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer */}
      <aside
        data-testid={QUOTE.drawer}
        aria-hidden={!drawerOpen}
        className={`fixed z-50 top-0 right-0 h-full w-full sm:w-[480px] bg-[var(--mw-bg)] border-l border-white/10 shadow-2xl overflow-y-auto transition-transform duration-500 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="sticky top-0 z-10 bg-[var(--mw-bg)]/95 backdrop-blur border-b border-white/10 px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Your Quote</div>
            <h2 className="font-display text-2xl mt-1 text-white">
              {items.length} item{items.length === 1 ? '' : 's'}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            data-testid={QUOTE.drawerClose}
            className="text-white/60 hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </header>

        {success ? (
          <div data-testid={QUOTE.drawerSuccess} className="px-6 py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[var(--mw-primary)]/15 flex items-center justify-center mb-5">
              <Check size={28} className="text-[var(--mw-primary)]" />
            </div>
            <h3 className="font-display text-2xl text-white mb-2">Quote sent</h3>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto">
              We&apos;ve received your request and sent you a copy. Our trade team will come back to you within one business day.
            </p>
            <button
              type="button"
              onClick={closeDrawer}
              className="mt-8 text-[11px] uppercase tracking-[0.22em] border border-white/15 hover:border-white text-white px-5 py-3 transition-colors"
            >
              Close
            </button>
          </div>
        ) : items.length === 0 ? (
          <div data-testid={QUOTE.drawerEmpty} className="px-6 py-16 text-center">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 mb-4">
              Empty
            </div>
            <h3 className="font-display text-2xl text-white">Your quote list is empty</h3>
            <p className="mt-3 text-white/55 text-sm max-w-xs mx-auto leading-relaxed">
              Browse the catalogue and add cuts to build a custom quote request.
            </p>
            <button
              type="button"
              onClick={() => {
                closeDrawer();
                setTimeout(() => document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' }), 60);
              }}
              className="mt-6 text-[11px] uppercase tracking-[0.22em] bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white px-6 py-3 transition-colors"
            >
              Browse catalogue
            </button>
          </div>
        ) : (
          <div>
            <ul data-testid={QUOTE.drawerItemsList} className="divide-y divide-white/10">
              {items.map((it) => (
                <li
                  key={it.product_id}
                  data-testid={QUOTE.itemRow(it.product_id)}
                  className="px-6 py-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-white leading-tight">
                        {it.product_name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mt-1">
                        {it.category}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(it.product_id)}
                      data-testid={QUOTE.itemRemove(it.product_id)}
                      className="text-white/45 hover:text-[var(--mw-primary)] transition-colors"
                      aria-label={`Remove ${it.product_name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    {/* qty controls */}
                    <div className="inline-flex items-center border border-white/15">
                      <button
                        type="button"
                        onClick={() => setQuantity(it.product_id, Math.max(0.5, it.quantity - 1))}
                        data-testid={QUOTE.itemDecrement(it.product_id)}
                        className="w-8 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        aria-label="Decrease"
                      >
                        <Minus size={12} />
                      </button>
                      <input
                        type="number"
                        min="0.1"
                        step="0.5"
                        value={it.quantity}
                        onChange={(e) => setQuantity(it.product_id, e.target.value)}
                        data-testid={QUOTE.itemQtyInput(it.product_id)}
                        className="w-16 h-9 bg-transparent text-white text-center text-sm outline-none border-x border-white/15 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(it.product_id, it.quantity + 1)}
                        data-testid={QUOTE.itemIncrement(it.product_id)}
                        className="w-8 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        aria-label="Increase"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    {/* unit toggle */}
                    <button
                      type="button"
                      onClick={() => setUnit(it.product_id, it.unit === 'kg' ? 'units' : 'kg')}
                      data-testid={QUOTE.itemUnitToggle(it.product_id)}
                      className="h-9 px-3 border border-white/15 hover:border-white text-[11px] uppercase tracking-[0.22em] text-white/80 transition-colors"
                    >
                      {it.unit}
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="px-6 py-3 flex items-center justify-between border-t border-white/10">
              <button
                type="button"
                onClick={clear}
                data-testid={QUOTE.drawerClearAll}
                className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[var(--mw-primary)] transition-colors"
              >
                Clear all
              </button>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">
                Prices sent via email
              </div>
            </div>

            {/* Contact form */}
            <form onSubmit={onSubmit} className="px-6 py-6 border-t border-white/10 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">
                Send this quote to us
              </div>
              <QField label="Business" testId={QUOTE.formBusiness} value={form.business_name} onChange={set('business_name')} required />
              <QField label="Your name" testId={QUOTE.formName} value={form.contact_name} onChange={set('contact_name')} required />
              <div className="grid grid-cols-2 gap-3">
                <QField label="Email" type="email" testId={QUOTE.formEmail} value={form.email} onChange={set('email')} required />
                <QField label="Phone" testId={QUOTE.formPhone} value={form.phone} onChange={set('phone')} required />
              </div>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">
                  Notes (optional)
                </span>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  data-testid={QUOTE.formNotes}
                  rows={2}
                  placeholder="Delivery window, packaging, cutting spec…"
                  className="input resize-none"
                />
              </label>

              {error && (
                <div className="border border-[var(--mw-primary)]/40 text-[var(--mw-primary)] text-xs uppercase tracking-[0.22em] px-4 py-3">
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || items.length === 0}
                data-testid={QUOTE.drawerSubmit}
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-60 text-white py-3.5 text-xs uppercase tracking-[0.22em] transition-colors"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                Send quote request
              </button>
            </form>
          </div>
        )}
      </aside>
    </>
  );
}

function QField({ label, testId, type = 'text', value, onChange, required }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-1.5">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        required={required}
        className="input"
      />
    </label>
  );
}
